import { getOrCreateClientOriginToken, ORIGIN_TOKEN_HEADER } from "@mixitone/oom";
import { type SupabaseClient, createClient } from "@supabase/supabase-js";
import type { Database as Db } from "../../../models/src/database.types";
import tauri from "./tauri";

export const supabaseOptions = {
  url: import.meta.env.VITE_SUPABASE_URL,
  anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
};

let supabase: SupabaseClient<Database>;

export type Database = Db;

function fetchWithOriginToken(nativeFetch: typeof fetch): typeof fetch {
  return (input, init) => {
    if (typeof window === "undefined") {
      return nativeFetch(input, init);
    }

    const headers = new Headers(init?.headers);
    headers.set(ORIGIN_TOKEN_HEADER, getOrCreateClientOriginToken());
    return nativeFetch(input, { ...init, headers });
  };
}

export function createSupabase(persistSession = true) {
  const nativeFetch = typeof window === "undefined" ? fetch : window.fetch;
  const globalFetch = fetchWithOriginToken(nativeFetch);

  // When running in the browser use localStorage. On Tauri use Tauri storage so
  // it can share the access token
  let authStorage: Storage | undefined;

  if (typeof window !== "undefined") {
    authStorage = window.localStorage;
  }

  if (supabase) return supabase;

  if (typeof window !== "undefined") {
    getOrCreateClientOriginToken();
  }

  const { url, anonKey } = supabaseOptions;
  supabase = createClient<Database>(url, anonKey, {
    global: {
      fetch: globalFetch,
    },
    auth: {
      // Tauri session lives in Rust; avoid localStorage/auto-refresh fighting setSession.
      persistSession: tauri.enabled ? false : persistSession,
      autoRefreshToken: !tauri.enabled,
      storage: authStorage,
    },
  });

  // supabase.auth.logDebugMessages = true;
  return supabase;
}

let crossSupabaseSync: Promise<SupabaseClient<Database>> | null = null;
let lastAppliedAccessToken: string | null = null;
/** Rust holds the real refresh token; Supabase JS still needs a non-empty placeholder. */
let tauriRefreshTokenPlaceholder = "na";

export function setTauriRefreshTokenPlaceholder(refreshToken: string | null | undefined) {
  tauriRefreshTokenPlaceholder = refreshToken?.trim() || "na";
  lastAppliedAccessToken = null;
}

/** After MFA verify on native, mark the JWT already applied so createCrossSupabase does not overwrite it. */
export function markTauriAccessTokenApplied(accessToken: string) {
  lastAppliedAccessToken = accessToken;
}

export function resetCrossSupabaseAuth() {
  lastAppliedAccessToken = null;
  crossSupabaseSync = null;
}

export async function createCrossSupabase() {
  if (crossSupabaseSync) {
    return crossSupabaseSync;
  }

  crossSupabaseSync = (async () => {
    const client = createSupabase();

    if (tauri.enabled) {
      const { access_token } = await tauri.invoke<{ access_token: string }>(
        "plugin:session|get_access_token",
      );
      if (access_token === lastAppliedAccessToken) {
        return client;
      }
      await client.auth.setSession({
        access_token,
        refresh_token: tauriRefreshTokenPlaceholder,
      });
      lastAppliedAccessToken = access_token;
      client.realtime.setAuth(access_token);
    }

    return client;
  })().finally(() => {
    crossSupabaseSync = null;
  });

  return crossSupabaseSync;
}

export async function sessionForWorker() {
  const auth = await createSupabase().auth.refreshSession();
  const session = auth?.data?.session;
  return session;
}
